import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import TopNav from "@/components/TopNav";
import api, { inr, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";

function loadRzp() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve();
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = resolve;
    s.onerror = () => reject(new Error("Could not load Razorpay checkout"));
    document.body.appendChild(s);
  });
}

export default function Billing() {
  const { t, tf } = useI18n();
  const { user, setUser } = useAuth();
  const [config, setConfig] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [modal, setModal] = useState(null); // {kind, pack_amount, label, total_paise}
  const [state_, setState_] = useState("");
  const [gstin, setGstin] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [c, l, i] = await Promise.all([
        api.get("/billing/config"),
        api.get("/billing/ledger"),
        api.get("/billing/invoices"),
      ]);
      setConfig(c.data);
      setLedger(l.data.entries || []);
      setInvoices(i.data.invoices || []);
      const me = await api.get("/auth/me");
      setUser(me.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, [setUser]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const plan = user?.plan || { active: false, type: null, expires_at: null };
  const balance = user?.wallet_balance || 0;

  const pay = async () => {
    if (!modal) return;
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        kind: modal.kind,
        pack_amount: modal.pack_amount || null,
        state: state_,
        gstin: gstin,
      });
      await loadRzp();
      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: "INR",
        name: "Jobky",
        description: modal.label,
        theme: { color: "#0f766e" },
        prefill: { name: user?.name, email: user?.email },
        handler: async (res) => {
          try {
            await api.post("/billing/verify", res);
            toast.success(t("Payment received — everything is activated."));
          } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
          }
          setModal(null);
          refresh();
        },
        modal: { ondismiss: () => { setBusy(false); setModal(null); toast(t("Checkout closed before payment.")); } },
      };
      if (data.mode === "order") options.order_id = data.order_id;
      else options.subscription_id = data.subscription_id;
      const rzp = new window.Razorpay(options);
      rzp.open();
      setBusy(false);
    } catch (e) {
      setBusy(false);
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const cancelSub = async () => {
    if (!window.confirm(t("Cancel your monthly subscription at the end of the current cycle?"))) return;
    try {
      const { data } = await api.post("/billing/cancel");
      toast.success(data.detail || t("Cancelled."));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const emailInvoice = async (id) => {
    try {
      await api.post(`/billing/invoices/${id}/email`);
      toast.success(t("Invoice emailed to you."));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const configured = config?.razorpay_configured;

  return (
    <div className="min-h-screen bg-bg" data-testid="billing-page">
      <TopNav active="billing" />
      <main className="mx-auto max-w-6xl px-5 py-12">
        {!configured && (
          <div className="mb-8 rounded-2xl border border-warn/40 bg-warn/10 p-4 text-sm text-warn" data-testid="razorpay-not-configured">
            {t("Payments are not switched on yet — the operator needs to add their Razorpay keys (Key ID, Secret and Webhook secret) in the app settings. Checkout buttons stay disabled until then; an operator can activate plans manually from the Admin panel.")}
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Plan card */}
          <section className="rounded-3xl border border-line bg-surface p-7 shadow-card lg:col-span-2" data-testid="plan-card">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted">{t("Your plan")}</p>
                <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight" data-testid="plan-status">
                  {plan.active ? t("Active") + (plan.type ? ` · ${t(plan.type)}` : "") : t("No active plan")}
                </h1>
                {plan.expires_at && (
                  <p className={`mt-1 text-sm ${plan.active ? "text-good" : "text-warn"}`} data-testid="plan-expires">
                    {plan.active
                      ? tf("Active until {date}", { date: `${new Date(plan.expires_at).getDate()} ${new Date(plan.expires_at).toLocaleString("en", { month: "short" })} ${new Date(plan.expires_at).getFullYear()}` })
                      : tf("Expired on {date}", { date: new Date(plan.expires_at).toLocaleDateString("en-IN") })}
                  </p>
                )}
              </div>
              {user?.role !== "admin" && (
                <div className="flex flex-col gap-2 sm:flex-row">
                  <button
                    disabled={!configured || busy}
                    onClick={() => setModal({ kind: "monthly", label: "Jobky monthly plan", total_paise: config?.monthly?.total })}
                    data-testid="subscribe-monthly-btn"
                    className="rounded-full bg-accent px-6 py-2.5 text-sm font-bold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-50"
                  >
                    {tf("Monthly {amount} + GST", { amount: "₹" + (config?.monthly?.price ?? 999) })}
                  </button>
                  <button
                    disabled={!configured || busy}
                    onClick={() => setModal({ kind: "yearly", label: "Jobky yearly plan", total_paise: config?.yearly?.total })}
                    data-testid="subscribe-yearly-btn"
                    className="rounded-full border-2 border-accent px-6 py-2.5 text-sm font-bold text-accent transition-colors hover:bg-accent hover:text-white disabled:opacity-50"
                  >
                    {tf("Yearly {amount} + GST", { amount: "₹" + (config?.yearly?.price ?? 9990) })}
                  </button>
                </div>
              )}
            </div>
            {plan.active && plan.type === "monthly" && (
              <button onClick={cancelSub} data-testid="cancel-subscription-btn" className="mt-5 text-sm text-muted underline hover:text-bad">
                {t("Cancel subscription (keeps access until expiry)")}
              </button>
            )}
            {user?.email_verified === false && (
              <div className="mt-5 flex flex-wrap items-center gap-3 rounded-xl bg-warn/10 p-3 text-sm text-warn" data-testid="verify-email-banner">
                {t("Verify your email to unlock AI features.")}
                <button
                  onClick={async () => { try { await api.post("/auth/resend-verification"); toast.success(t("Verification email sent.")); } catch (e) { toast.error(t("Could not send — try later.")); } }}
                  data-testid="resend-verification-btn"
                  className="rounded-full border border-warn/50 px-3 py-1 text-xs font-bold hover:bg-warn hover:text-white"
                >
                  {t("Resend email")}
                </button>
              </div>
            )}
          </section>

          {/* Wallet card */}
          <section className="rounded-3xl border border-line bg-night p-7 text-night-ink shadow-card" data-testid="wallet-card">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-night-soft">{t("Wallet balance")}</p>
            <p className="mt-2 font-display text-4xl font-extrabold" data-testid="wallet-balance">{inr(balance)}</p>
            <p className="mt-2 text-xs text-night-soft">{t("Credit expires 12 months after each recharge. Used for paid actions as they go live.")}</p>
          </section>
        </div>

        {/* Packs */}
        <section className="mt-12" data-testid="packs-section">
          <h2 className="font-display text-2xl font-extrabold tracking-tight">{t("Recharge your wallet")}</h2>
          <p className="mt-1 text-sm text-muted">{t("The pack amount (before GST) is added to your wallet. Buy as many as you want, any time.")}</p>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {(config?.packs || []).map((p, i) => (
              <div key={p.price} className="rounded-2xl border border-line bg-surface p-6 shadow-card transition-transform hover:-translate-y-1" data-testid={`pack-card-${i}`}>
                <p className="font-display text-3xl font-extrabold">₹{p.price.toLocaleString("en-IN")}</p>
                <p className="mt-1 text-xs text-muted">{tf("+ 18% GST · you pay {amount}", { amount: "₹" + (p.total / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 }) })}</p>
                <p className="mt-3 text-sm font-bold text-accent">{tf("Wallet +{amount}", { amount: "₹" + p.price.toLocaleString("en-IN") })}</p>
                <button
                  disabled={!configured || busy}
                  onClick={() => setModal({ kind: "pack", pack_amount: p.price, label: `Wallet pack ₹${p.price}`, total_paise: p.total })}
                  data-testid={`pack-buy-${i}`}
                  className="mt-4 w-full rounded-full bg-accent px-4 py-2 text-sm font-bold text-white transition-colors hover:brightness-110 disabled:opacity-50"
                >
                  {t("Recharge")}
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Ledger */}
        <section className="mt-14" data-testid="ledger-section">
          <h2 className="font-display text-2xl font-extrabold tracking-tight">{t("Wallet ledger")}</h2>
          <div className="mt-4 overflow-x-auto rounded-2xl border border-line bg-surface">
            <table className="w-full text-left text-sm" data-testid="ledger-table">
              <thead>
                <tr className="border-b border-line font-mono text-xs uppercase tracking-wider text-muted">
                  <th className="px-4 py-3">{t("Date")}</th>
                  <th className="px-4 py-3">{t("Reason")}</th>
                  <th className="px-4 py-3 text-right">{t("Amount")}</th>
                  <th className="px-4 py-3 text-right">{t("Balance after")}</th>
                </tr>
              </thead>
              <tbody className="font-mono">
                {ledger.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-6 text-center text-muted">{t("No wallet activity yet — credit packs you buy will show up here.")}</td></tr>
                )}
                {ledger.map((e) => (
                  <tr key={e.id} className="border-b border-line/60 last:border-0">
                    <td className="px-4 py-3 text-muted">{(e.ts || "").slice(0, 10)}</td>
                    <td className="px-4 py-3 font-sans">{e.reason}</td>
                    <td className={`px-4 py-3 text-right ${e.amount >= 0 ? "text-good" : "text-bad"}`}>
                      {e.amount >= 0 ? "+" : "−"}{inr(Math.abs(e.amount))}
                    </td>
                    <td className="px-4 py-3 text-right">{inr(e.balance_after)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Invoices */}
        <section className="mt-14" data-testid="invoices-section">
          <h2 className="font-display text-2xl font-extrabold tracking-tight">{t("GST invoices")}</h2>
          <div className="mt-4 overflow-x-auto rounded-2xl border border-line bg-surface">
            <table className="w-full text-left text-sm" data-testid="invoices-table">
              <thead>
                <tr className="border-b border-line font-mono text-xs uppercase tracking-wider text-muted">
                  <th className="px-4 py-3">{t("Number")}</th>
                  <th className="px-4 py-3">{t("Date")}</th>
                  <th className="px-4 py-3">{t("For")}</th>
                  <th className="px-4 py-3 text-right">{t("Total")}</th>
                  <th className="px-4 py-3 text-right">{t("Actions")}</th>
                </tr>
              </thead>
              <tbody className="font-mono">
                {invoices.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-6 text-center text-muted">{t("No invoices yet.")}</td></tr>
                )}
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-line/60 last:border-0">
                    <td className="px-4 py-3">{inv.number}</td>
                    <td className="px-4 py-3 text-muted">{(inv.ts || "").slice(0, 10)}</td>
                    <td className="px-4 py-3 font-sans">{inv.description}</td>
                    <td className="px-4 py-3 text-right">{inr(inv.total)}</td>
                    <td className="px-4 py-3 text-right">
                      <a href={`/api/billing/invoices/${inv.id}/pdf`} data-testid={`invoice-download-${inv.number}`} className="mr-3 font-bold text-accent hover:underline">PDF</a>
                      <button onClick={() => emailInvoice(inv.id)} data-testid={`invoice-email-${inv.number}`} className="text-accent hover:underline">{t("Email me")}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>

      {/* Checkout modal */}
      {modal && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-ink/50 p-4 backdrop-blur-sm" data-testid="checkout-modal">
          <div className="w-full max-w-md rounded-3xl border border-line bg-surface p-7 shadow-lift">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted">{t("Checkout")}</p>
            <h3 className="mt-2 font-display text-2xl font-extrabold">{modal.label}</h3>
            <p className="mt-1 text-sm text-muted">
              {config?.business_name} · {tf("GST invoice included · You pay {amount} (incl. 18% GST)", { amount: "₹" + (modal.total_paise / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 }) })}
            </p>
            <div className="mt-5 space-y-4">
              <div>
                <label htmlFor="co-state" className="mb-1.5 block font-mono text-xs uppercase tracking-[0.15em] text-muted">{t("State (for GST)")}</label>
                <select id="co-state" value={state_} onChange={(e) => setState_(e.target.value)} data-testid="checkout-state-select"
                  className="w-full rounded-xl border border-line bg-surface px-4 py-3 text-sm outline-none focus:border-accent">
                  <option value="">{t("Select your state")}</option>
                  {(config?.states || []).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="co-gstin" className="mb-1.5 block font-mono text-xs uppercase tracking-[0.15em] text-muted">{t("GSTIN (optional)")}</label>
                <input id="co-gstin" value={gstin} onChange={(e) => setGstin(e.target.value)} data-testid="checkout-gstin-input"
                  placeholder="e.g. 27ABCDE1234F1Z5"
                  className="w-full rounded-xl border border-line bg-surface px-4 py-3 text-sm outline-none focus:border-accent" />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setModal(null)} data-testid="checkout-cancel" className="flex-1 rounded-full border border-line px-4 py-3 text-sm font-bold hover:border-accent hover:text-accent">
                  {t("Cancel")}
                </button>
                <button onClick={pay} disabled={busy || !state_} data-testid="checkout-pay"
                  className="flex-1 rounded-full bg-accent px-4 py-3 text-sm font-bold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-50">
                  {busy ? t("Opening…") : t("Pay with Razorpay")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
